import { ReactElement, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Row } from 'react-table';
import { QueryActivityPageSetup } from '../../../api/schema';
import { CopyTsvButton } from '../../../component/copyTsvButton/CopyTsvButton';
import Table from '../../../component/table/Table';
import { tagsDate } from '../../../helpers/dataHelper';

type SetupsListProps = {
  setups: QueryActivityPageSetup[];
  fetching: boolean;
};

type RowData = {
  key: string;
  site: string;
  site_id: string;
  job_number: string;
  prepared_at: string;
  delivered_at: string;
  item_count: number;
};

/**
 * The job sites set up for delivery in this period: equipment allocated to a
 * job and handed to the site in the system, which is the step a contractor's
 * physical delivery follows. Prepared is when the equipment was set aside.
 */
export const SetupsList = ({ setups, fetching }: SetupsListProps): ReactElement => {
  const rows = useMemo<RowData[]>(
    () =>
      setups.map((setup) => ({
        key: setup.assignment_id || '',
        site: setup.site_name || '',
        site_id: setup.site_id || '',
        job_number: setup.job_number || '',
        prepared_at: tagsDate(setup.prepared_at) || '',
        delivered_at: tagsDate(setup.delivered_at) || '',
        item_count: setup.item_count || 0,
      })),
    [setups],
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Job Site',
        accessor: 'site',
        ellipsis: true,
        disableFilters: true,
        Cell: ({ row }: { row: { original: RowData } }) => <Link to={`/customers/job-sites/${row.original.site_id}/details`}>{row.original.site}</Link>,
      },
      { Header: 'Job #', accessor: 'job_number', disableFilters: true },
      { Header: 'Prepared', accessor: 'prepared_at', disableFilters: true },
      { Header: 'Set up', accessor: 'delivered_at', disableFilters: true },
      {
        Header: 'Equipment',
        accessor: 'item_count',
        disableFilters: true,
        cellStyle: { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' },
      },
    ],
    [],
  );

  return (
    <Table<RowData>
      title="Job Sites Set Up"
      columns={columns}
      data={rows}
      loading={fetching}
      size="small"
      withPagination={false}
      noGlobalFilter
      rowIdKey="key"
      noDataText="No job sites set up in this period"
      // The row opens the job site. Table ignores a click that landed on a
      // link, so the name anchor still works on its own.
      getRowProps={(row: Row) => ({ link: `/customers/job-sites/${(row.original as RowData).site_id}/details` })}
      totals={{ item_count: rows.reduce((sum, row) => sum + row.item_count, 0) }}
      headerAddon={
        <CopyTsvButton
          label="Job sites set up"
          header={['Job Site', 'Job #', 'Prepared', 'Set up', 'Equipment']}
          rows={rows.map((row) => [row.site, row.job_number, row.prepared_at, row.delivered_at, row.item_count])}
          disabled={fetching || !rows.length}
        />
      }
    />
  );
};

export default SetupsList;
